import React, { useRef, useState } from 'react';
import '../assets/styles/Contact.scss';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

function Contact() {
  const form = useRef<HTMLFormElement>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [buttonText, setButtonText] = useState('Send');
  const [isSuccess, setIsSuccess] = useState(false);

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [messageError, setMessageError] = useState(false);

  // Simple email regex for validation
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validate phone: allows digits, +, -, spaces, and brackets (e.g. +91 98765 43210)
  const validatePhone = (phone: string) => {
    const cleaned = phone.replace(/[\s\-()]/g, '');
    return /^\+?[0-9]{7,15}$/.test(cleaned);
  };

  // Handle phone input change - allow digits, +, -, spaces, and brackets
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^[0-9+\s\-()\b]+$/.test(val)) {
      setPhone(val);
    }
  };

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isNameValid = name.trim() !== '';
    const isEmailValid = validateEmail(email.trim());
    const isPhoneValid = validatePhone(phone.trim());
    const isMessageValid = message.trim() !== '';

    setNameError(!isNameValid);
    setEmailError(!isEmailValid);
    setPhoneError(!isPhoneValid);
    setMessageError(!isMessageValid);

    if (isNameValid && isEmailValid && isPhoneValid && isMessageValid) {
      setLoading(true);
      setButtonText('Sending...');
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            access_key: 'fceaa564-f31e-4f66-b70c-81d7b9a551a2',
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim(),
            message: message.trim(),
            subject: `Portfolio Inquiry from ${name.trim()}`
          })
        });

        const data = await response.json();

        if (data.success) {
          setIsSuccess(true);
          setButtonText('Sent!');
          setName('');
          setEmail('');
          setPhone('');
          setMessage('');

          setTimeout(() => {
            setButtonText('Send');
            setIsSuccess(false);
          }, 3500);
        } else {
          setButtonText('Send');
        }
      } catch (error: any) {
        console.error('Web3Forms send error:', error);
        setButtonText('Send');
      } finally {
        setLoading(false);
      }
    }
  };


  return (
    <div id="contact">
      <div className="items-container">
        <div className="contact_wrapper">
          <h1>Contact Me</h1>
          <p>Got a project waiting to be realized? Let's collaborate and make it happen!</p>

          <Box
            component="form"
            ref={form}
            noValidate
            autoComplete="off"
            onSubmit={sendEmail}
            className="contact-form"
          >
            <div className="form-flex">
              <TextField
                required
                label="Your Name"
                name="name"
                placeholder="What's your name?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={nameError}
                helperText={nameError ? 'Please enter your name' : ''}
              />
              <TextField
                required
                label="Email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={emailError}
                helperText={emailError ? 'Please enter a valid email' : ''}
              />
              <TextField
                required
                label="Phone Number"
                name="phone"
                placeholder="Enter your phone number"
                value={phone}
                onChange={handlePhoneChange}
                error={phoneError}
                helperText={phoneError ? 'Please enter a valid phone number (digits only)' : ''}
                inputProps={{ maxLength: 15 }}
              />
            </div>

            <TextField
              required
              label="Message"
              name="message"
              placeholder="Send me any inquiries or questions"
              multiline
              rows={10}
              className="body-form"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={messageError}
              helperText={messageError ? 'Please enter the message' : ''}
            />

            <div className="button-wrapper">
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                color={isSuccess ? 'success' : 'primary'}
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : isSuccess ? <CheckIcon /> : <SendIcon />}
              >
                {buttonText}
              </Button>
            </div>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default Contact;
